// UI-level presentation helpers for the branded-fare-upsell components.
// Offer-shape utilities live in @liveflight/utilities/flight-offer;
// fare-choice pipeline helpers live on the provider — no re-exports here.

import {
  bag_quantity,
  guaranteed_bag_allowance,
} from '@liveflight/providers/base/helpers';

export const show_amenity = (amenity) => !amenity.isChargeable;

const INFERABLE_AMENITIES = {
  'INF:BACKPACK': {
    sortOrder: 0,
    code: 'INF:BACKPACK',
    description: (quantity = 1) =>
      `${quantity > 1 ? `${quantity} ` : ''}Bagagli${quantity === 1 ? 'o' : ''} personal${quantity === 1 ? 'e' : 'i'} piccol${quantity === 1 ? 'o' : 'i'}`,
    icon: 'backpack',
    tooltip: 'Dimensioni da controllare sul sito della compagnia',
  },
  'INF:CABIN': {
    sortOrder: 1,
    code: 'INF:CABIN',
    description: (quantity = 1) =>
      `${quantity > 1 ? `${quantity} ` : ''}Bagagli${quantity === 1 ? 'o' : ''} a mano`,
    icon: 'trolley',
    tooltip: 'Dimensioni da controllare sul sito della compagnia',
  },
  'INF:CHECKED': {
    sortOrder: 2,
    code: 'INF:CHECKED',
    description: (quantity = 1) =>
      `${quantity > 1 ? `${quantity} ` : ''}Bagagli${quantity === 1 ? 'o' : ''} in stiva`,
    icon: 'luggage',
    tooltip: 'Dimensioni da controllare sul sito della compagnia',
  },
};

const ALLOWANCE_READERS = {
  'INF:BACKPACK': (fare_detail) => fare_detail.includedHandBags,
  'INF:CABIN': (fare_detail) => fare_detail.includedCabinBags,
  'INF:CHECKED': (fare_detail) => fare_detail.includedCheckedBags,
};

const add_bag_details = (key, details) => {
  const quantity = bag_quantity(details);
  if (!key || quantity === 0) {
    return null;
  }
  // Clone the template — never mutate the shared INFERABLE_AMENITIES entry,
  // or a tooltip deleted for one offer (below) would leak to the next.
  const item = { ...INFERABLE_AMENITIES[key] };
  item.formatted = item.description(quantity);
  if (details.weight && details.weightUnit) {
    item.formatted = `${item.formatted} ${details.weight}${details.weightUnit}`;
    delete item.tooltip;
  }
  return item;
};

export const infer_included_bags_as_amenities = (flight_offer) => {
  const inferred = Object.keys(ALLOWANCE_READERS).map((key) =>
    add_bag_details(
      key,
      guaranteed_bag_allowance(flight_offer, ALLOWANCE_READERS[key])
    )
  );

  const amenities = inferred
    .filter((amenity) => !!amenity)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((amenity) => ({
      ...amenity,
      description: amenity.formatted,
      amenityType: 'BAGGAGE',
      isChargeable: false,
      isSynthetic: true,
    }));
  return amenities;
};

/**
 * Amenities for a single offer: the carrier-supplied ones on the first
 * segment, or — when the fare ships none — synthetic inferred-bag placeholders.
 *
 * @param {Object} flight_offer - The offer to read amenities from.
 * @returns {Array<Object>} The offer's amenities (possibly inferred), or [].
 */
export const get_offer_amenities = (flight_offer) => {
  const { travelerPricings = [] } = flight_offer || {};
  const { fareDetailsBySegment = [] } = travelerPricings.length
    ? travelerPricings[0]
    : {};
  const { amenities: raw_amenities } = fareDetailsBySegment[0] || {};
  const amenities = Array.isArray(raw_amenities) ? raw_amenities : [];
  if (amenities.length) {
    return amenities;
  }
  return infer_included_bags_as_amenities(flight_offer);
};

/**
 * Builds the per-offer map of included (non-chargeable) amenities. When the
 * only distinct amenity across every offer is a lone synthetic placeholder it
 * isn't worth surfacing, so every offer is emptied.
 *
 * @param {Array<Object>} flight_offers - Offers to map, each with an `id`.
 * @returns {Object<string, Array<Object>>} offer id → included amenities.
 */
export const map_included_amenities = (flight_offers = []) => {
  const offers_amenities = {};
  const distinct = [];

  flight_offers.forEach((offer) => {
    const included = get_offer_amenities(offer).filter(show_amenity);
    offers_amenities[offer.id] = included;
    included.forEach((amenity) => {
      if (!distinct.some((a) => a.code === amenity.code)) {
        distinct.push(amenity);
      }
    });
  });

  if (distinct.length === 1 && distinct[0].isSynthetic) {
    flight_offers.forEach((offer) => {
      offers_amenities[offer.id] = [];
    });
  }

  return offers_amenities;
};
